import React, { useEffect, useState } from "react";
import {
  FormControlLabel,
  Checkbox,
  Box,
  Radio,
  RadioGroup,
  IconButton,
  Typography,
  FormHelperText,
  Button,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSelector } from "react-redux";
import { selectUserDetail } from "../redux/slice/userSlice";
import { replaceDescriptionPlaceholders, renderMarkdownBold } from "../components/enum/descriptionUtils";

const INSURANCE_FIELD_ID = "root_insurance_insuranceTerm";

const insurancePolicyOptions = [
  {
    value: "upload_now",
    label: "I agree to upload my Long Term Care Insurance policy now",
  },
  {
    value: "email_within_14_business_days",
    label:
      "I agree to email a copy of my Long Term Care Insurance policy within 14 business days",
  },
  {
    value: "provide_to_community_liaison",
    label:
      "I agree to provide the policy to the Community Liaison after onboarding",
  },
];

const MAX_POLICY_DOCUMENTS = 5;
const MAX_FILE_UPLOAD_SIZE_MB =
  Number(process.env.REACT_APP_MAX_FILE_UPLOAD_SIZE_MB) || 10;
const MAX_FILE_UPLOAD_SIZE_BYTES = MAX_FILE_UPLOAD_SIZE_MB * 1024 * 1024;
const ALLOWED_POLICY_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
];
const ALLOWED_POLICY_FILE_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];

const AgreementCheckboxField = ({
  schema,
  idSchema,
  formData,
  onChange,
  errorSchema = {},
  uiSchema = {},
  formContext = {},
}) => {
  const { title, description } = schema;
  const uiDescription = uiSchema["ui:description"];
  const fieldId = idSchema.$id;
  const isInsuranceTerm = fieldId === INSURANCE_FIELD_ID;
  const windowPath = window.location.pathname;
  const getUserdetail = useSelector(selectUserDetail);
  const roles = getUserdetail?.roles;
  const isInsuranceDisable =
    (roles !== null || roles !== undefined) &&
    windowPath.toLowerCase() !== "/clientintake";

  const filePath = process.env.REACT_APP_S3BUCKET_FILEURL;

  const { policyFiles = [], setPolicyFiles } = formContext;
  const resolvedDescription = replaceDescriptionPlaceholders(
    description,
    formContext?.formData,
  );
  const resolvedUiDescription = replaceDescriptionPlaceholders(
    uiDescription,
    formContext?.formData,
  );

  const parseAgreed = (value) => {
    if (value === true || value === "true") return true;
    if (value === false || value === "false") return false;
    return null; // covers null, undefined, "", any other string
  };

  const defaultInsuranceData = {
    agreed: null,
    policyDeliveryOption: "",
    policyDocuments: [],
  };

  const normalizeInsuranceTerm = (formData) => {
    if (formData === true || formData === "true") {
      return { ...defaultInsuranceData, agreed: true };
    }

    if (formData === false || formData === "false") {
      return { ...defaultInsuranceData, agreed: false };
    }

    if (formData == null) {
      return defaultInsuranceData;
    }

    if (typeof formData === "object") {
      return {
        ...defaultInsuranceData,
        ...formData,
      };
    }

    return formData;
  };

  const normalizedFormData = isInsuranceTerm
    ? normalizeInsuranceTerm(formData)
    : formData;

  const isObjectValue =
    normalizedFormData &&
    typeof normalizedFormData === "object" &&
    !Array.isArray(normalizedFormData);

  const isAgreed = isObjectValue
    ? parseAgreed(normalizedFormData.agreed) === true
    : normalizedFormData === true;

  const selectedPolicyOption = isObjectValue
    ? normalizedFormData.policyDeliveryOption || ""
    : "";

  const uploadedDocuments = isObjectValue
    ? (normalizedFormData.policyDocuments || []).filter(
        (doc) => doc && typeof doc === "object" && doc.name, //&& doc.content,
      )
    : [];

  const isNA =
    isObjectValue && parseAgreed(normalizedFormData.agreed) === false;

  const policyOptionError =
    !isNA && errorSchema?.policyDeliveryOption?.__errors?.[0];

  const agreementError =
    !isNA &&
    (errorSchema?.agreed?.__errors?.[0] ||
      errorSchema?.__errors?.[0] ||
      (Array.isArray(errorSchema?.agreed) ? errorSchema.agreed[0] : null));

  const documentError = !isNA && errorSchema?.policyDocuments?.__errors?.[0];

  const [, setTouched] = useState(false);
  const [fileError, setFileError] = useState("");

  const handleChange = (event) => {
    const checked = event.target.checked;
    setTouched(true);

    if (!isInsuranceTerm) {
      onChange(checked ? true : undefined);
      return;
    }

    if (isInsuranceDisable) {
      onChange({
        ...normalizedFormData,
        agreed: checked ? true : null,
      });
    } else if (checked) {
      onChange({
        agreed: true,
        policyDeliveryOption: normalizedFormData?.policyDeliveryOption || "",
        policyDocuments: normalizedFormData?.policyDocuments || [],
      });
    } else {
      onChange({
        agreed: null,
        policyDeliveryOption: "",
        policyDocuments: [],
      });
    }
    setFileError("");
  };

  useEffect(() => {
    if (selectedPolicyOption !== "upload_now") {
      setFileError("");
    }
  }, [selectedPolicyOption]);

  const handleNAChange = (event) => {
    const checked = event.target.checked;
    setTouched(true);

    if (isInsuranceDisable) {
      onChange({
        ...normalizedFormData,
        agreed: checked ? false : null,
      });
    } else if (checked) {
      onChange({
        agreed: false,
        policyDeliveryOption: "",
        policyDocuments: [],
      });
    } else {
      onChange({
        agreed: null,
        policyDeliveryOption: "",
        policyDocuments: [],
      });
    }
    setFileError("");
  };

  const handlePolicyOptionChange = (event) => {
    const policyDeliveryOption = event.target.value;

    setFileError("");
    setTouched(true);

    onChange({
      ...(isObjectValue ? normalizedFormData : {}),
      agreed: true,
      policyDeliveryOption,
      policyDocuments: [],
    });
  };

  const handleDocumentUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";

    if (selectedFiles.length === 0) {
      return;
    }

    if (
      uploadedDocuments.length + selectedFiles.length >
      MAX_POLICY_DOCUMENTS
    ) {
      setTouched(true);
      setFileError(`You can upload up to ${MAX_POLICY_DOCUMENTS} files.`);
      return;
    }

    const oversizedFile = selectedFiles.find(
      (file) => file.size > MAX_FILE_UPLOAD_SIZE_BYTES,
    );

    if (oversizedFile) {
      setTouched(true);
      setFileError(
        `Each file must be ${MAX_FILE_UPLOAD_SIZE_MB} MB or smaller.`,
      );
      return;
    }

    const unsupportedFile = selectedFiles.find((file) => {
      const fileName = file.name.toLowerCase();
      return (
        !ALLOWED_POLICY_FILE_TYPES.includes(file.type) &&
        !ALLOWED_POLICY_FILE_EXTENSIONS.some((extension) =>
          fileName.endsWith(extension),
        )
      );
    });

    if (unsupportedFile) {
      setTouched(true);
      setFileError("Policy files must be PDF, JPG, or PNG.");
      return;
    }

    try {
      const newDocuments = selectedFiles.map((file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
      }));

      setPolicyFiles((prev) => [...prev, ...selectedFiles]);

      setFileError("");
      setTouched(true);
      onChange({
        ...(isObjectValue ? normalizedFormData : {}),
        agreed: true,
        policyDeliveryOption: "upload_now",
        policyDocuments: [...uploadedDocuments, ...newDocuments],
      });
    } catch {
      setTouched(true);
      setFileError("Unable to read the selected file. Please try again.");
    }
  };

  const handleDocumentDelete = (event, documentIndex) => {
    event.preventDefault();
    event.stopPropagation();

    const nextDocuments = uploadedDocuments.filter(
      (_, index) => index !== documentIndex,
    );

    const nextValue = {
      ...(isObjectValue ? normalizedFormData : {}),
      agreed: true,
      policyDeliveryOption: "upload_now",
    };

    nextValue.policyDocuments = nextDocuments;

    delete nextValue.policyDocument;

    setFileError("");
    setTouched(true);
    onChange(nextValue);
  };

  const handleDocumentView = (event, policyDocument) => {
    event.preventDefault();
    event.stopPropagation();

    if (policyDocument.isExisting && policyDocument.filePath && filePath) {
      const policyPath = `${filePath.replace(/\/$/, "")}/${policyDocument.filePath.replace(/^\//, "")}`;
      window.open(policyPath, "_blank");
      return;
    }

    const file = policyFiles.find(
      (f) =>
        f.name === policyDocument.name &&
        f.size === policyDocument.size &&
        f.type === policyDocument.type,
    );

    if (!file) {
      console.error("File not available — component may have remounted");
      return;
    }

    const url = URL.createObjectURL(file);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Box mb={0}>
      {description && (
        <Box mt={1} mb={1} sx={{ whiteSpace: "pre-wrap" }}>
          {typeof description === "string" ? (
            <div>{renderMarkdownBold(resolvedDescription)}</div>
          ) : (
            resolvedDescription
          )}
        </Box>
      )}
      {!description && uiDescription && (
        <Box mt={1} mb={1} sx={{ whiteSpace: "pre-wrap" }}>
          {resolvedUiDescription}
        </Box>
      )}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          gap: 15,
          alignItems: "center",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              id={`${fieldId}-agree`}
              checked={isAgreed}
              onChange={handleChange}
              onBlur={() => setTouched(true)}
            />
          }
          label={title}
        />

        {isInsuranceTerm && (
          <FormControlLabel
            control={
              <Checkbox
                id={`${fieldId}-na`}
                checked={isNA}
                onChange={handleNAChange}
                onBlur={() => setTouched(true)}
              />
            }
            label="Not Applicable"
          />
        )}
      </Box>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          flexDirection: "column",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              id={`${fieldId}-agree`}
              checked={isAgreed}
              onChange={handleChange}
              onBlur={() => setTouched(true)}
            />
          }
          label={title}
        />
      </Box>
      {isInsuranceTerm && isAgreed && (
        <RadioGroup
          name={`${fieldId}-policy-options`}
          value={selectedPolicyOption}
          onChange={handlePolicyOptionChange}
          sx={{
            ml: { xs: 1, sm: 3 },
            mt: 1,
          }}
        >
          {insurancePolicyOptions.map((option) => {
            const isUploadOption = option.value === "upload_now";

            return (
              <Box key={option.value}>
                <FormControlLabel
                  value={option.value}
                  control={<Radio />}
                  disabled={isInsuranceDisable}
                  label={<Typography>{option.label}</Typography>}
                />

                {isUploadOption &&
                  isAgreed &&
                  selectedPolicyOption === "upload_now" && (
                    <Box
                      sx={{
                        ml: { xs: 2, sm: 5 },
                        mt: 1,
                        mb: 2,
                      }}
                    >
                      {!isInsuranceDisable && (
                        <>
                          <Button
                            component="label"
                            variant="contained"
                            startIcon={<UploadFileIcon />}
                            sx={{
                              width: {
                                xs: "100%",
                                sm: 300,
                              },
                              maxWidth: 300,
                              justifyContent: "center",
                              textTransform: "none",
                            }}
                          >
                            Upload File
                            <input
                              hidden
                              multiple
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                              onChange={handleDocumentUpload}
                            />
                          </Button>
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{
                              display: "block",
                              mt: 0.5,
                              maxWidth: 300,
                              lineHeight: 1,
                            }}
                          >
                            Note: Up to 5 files (10 MB each)
                          </Typography>
                        </>
                      )}

                      {uploadedDocuments.length > 0 && (
                        <Box mt={2}>
                          {uploadedDocuments.map((document, index) => (
                            <Box
                              key={`${document.name}-${index}`}
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={1}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  wordBreak: "break-word",
                                  maxWidth: {
                                    xs: "100%",
                                    sm: 300,
                                  },
                                }}
                              >
                                {document.name}
                              </Typography>

                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(event) =>
                                  handleDocumentView(event, document)
                                }
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>

                              {!isInsuranceDisable && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(event) =>
                                    handleDocumentDelete(event, index)
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
              </Box>
            );
          })}
        </RadioGroup>
      )}
      {isInsuranceTerm && (
        <Box sx={{ display: { xs: "block", sm: "none" }, mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                id={`${fieldId}-na`}
                checked={isNA}
                onChange={handleNAChange}
                onBlur={() => setTouched(true)}
              />
            }
            label="Not Applicable"
          />
        </Box>
      )}

      {fileError && <FormHelperText error>{fileError}</FormHelperText>}

      {isInsuranceTerm && isAgreed && !isNA && policyOptionError && (
        <FormHelperText error>{policyOptionError}</FormHelperText>
      )}

      {!isNA && agreementError && (
        <FormHelperText error>{agreementError}</FormHelperText>
      )}

      {!isNA &&
        !fileError &&
        uploadedDocuments.length === 0 &&
        documentError && <FormHelperText error>{documentError}</FormHelperText>}
    </Box>
  );
};

export default AgreementCheckboxField;
